import { addDays } from "date-fns";
import axios from "axios";
import { apiUrl } from "../config/appConfig";
import client from "../graphql/client";
import { REFRESH_TOKEN } from "../graphql/mutations/Auth";

export const setSession = ({ token, businessProfile }) => {
  if (typeof localStorage !== "undefined") {
    const { accessToken, refreshToken } = token;
    localStorage.setItem("token", accessToken);
    //Setting cookie as well
    setTokenCookie(accessToken, 2);
    localStorage.setItem("refresh", refreshToken);
    localStorage.setItem("expireAt", addDays(new Date(), 1).getTime()); //add one day as expiry time
    // localStorage.setItem("expireAt", Date.now() + 30 * 1000);
    localStorage.setItem("business_profile", "true");
    localStorage.setItem("isEmployee", false);
  }
};

//set session of employee using GraphQL response
export const setSessionEmployee = (employeeLoginData) => {
  if (typeof localStorage !== "undefined") {
    const { accessToken, refreshToken } = employeeLoginData.token;
    localStorage.setItem("token", accessToken);
    //Setting cookie as well
    setTokenCookie(accessToken, 2);
    localStorage.setItem("refresh", refreshToken);
    localStorage.setItem("expireAt", addDays(new Date(), 1).getTime()); //add one day as expiry time

    // Store employee and business profile information
    if (employeeLoginData.businessProfile) {
      localStorage.setItem("business_profile", JSON.stringify(employeeLoginData.businessProfile));
    }
    if (employeeLoginData.employeeProfile) {
      localStorage.setItem("employee_profile", JSON.stringify(employeeLoginData.employeeProfile));
    }

    localStorage.setItem("isEmployee", true);
    localStorage.setItem("hasBusinessProfile", employeeLoginData.hasBusinessProfile || false);
    localStorage.setItem("hasEmployeeProfile", employeeLoginData.hasEmployeeProfile || false);

    // Store profile image if available
    if (employeeLoginData.profileImage) {
      localStorage.setItem("profile_image", employeeLoginData.profileImage);
    }
  }
};

//set session of employee using REST API (legacy - commented)
/*
export const setSessionEmployee = ({ token, business_profile_id, employee_id }) => {
  if (typeof localStorage !== "undefined") {
    const { access, refresh } = token;
    localStorage.setItem("token", access);
    //Setting cookie as well
    setTokenCookie(access, 2);
    localStorage.setItem("refresh", refresh);
    localStorage.setItem("expireAt", addDays(new Date(), 1).getTime()); //add one day as expiry time
    // localStorage.setItem("expireAt", Date.now() + 30 * 1000);
    localStorage.setItem("business_profile", business_profile_id);
    localStorage.setItem("isEmployee", true);
  }
};
*/

export const clearSession = () => {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem("token");
    removeTokenCookie();
    localStorage.removeItem("refresh");
    localStorage.removeItem("expireAt");
    localStorage.removeItem("business_profile");
    localStorage.removeItem("profile_image");
    localStorage.removeItem("isEmployee");
    localStorage.removeItem("employee_profile");
    localStorage.removeItem("hasBusinessProfile");
    localStorage.removeItem("hasEmployeeProfile");
    
    // Clear all AI chat sessions from sessionStorage (user-scoped)
    // Get all keys from sessionStorage and remove AI chat-related ones
    if (typeof sessionStorage !== "undefined") {
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (key.startsWith("aiChatSessions_") || key.startsWith("currentAISessionId_"))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    }
  }
};

// Helper function to check if current user is an employee
export const isEmployeeSession = () => {
  return localStorage.getItem("isEmployee") === "true";
};

// Helper function to get employee profile from session
export const getEmployeeProfile = () => {
  const employeeProfileStr = localStorage.getItem("employee_profile");
  if (employeeProfileStr) {
    try {
      return JSON.parse(employeeProfileStr);
    } catch (error) {
      console.error("Error parsing employee profile from localStorage:", error);
      return null;
    }
  }
  return null;
};

// Helper function to get business profile from session
export const getBusinessProfile = () => {
  const businessProfileStr = localStorage.getItem("business_profile");
  if (businessProfileStr && businessProfileStr !== "true") {
    try {
      return JSON.parse(businessProfileStr);
    } catch (error) {
      console.error("Error parsing business profile from localStorage:", error);
      return null;
    }
  }
  return null;
};

//Function to set cookie to show user information in next.js landing page.
export const setTokenCookie = (token, days = 7) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000); // Set the expiration in days
  const expires = `expires=${date.toUTCString()}`;

  // Check if we are in production (secure cookie and cross-domain for production)
  const isProduction = window.location.hostname !== "localhost";

  let cookieString = `token=${token}; path=/; ${expires}; SameSite=None`;

  // Add domain and Secure flag only in production
  if (isProduction) {
    cookieString += `; domain=.theprofit.ai; Secure`;
  }

  document.cookie = cookieString;
};

//Function to remove token cookie
export const removeTokenCookie = () => {
  // Set the token cookie with an expired date to remove it
  const isProduction = window.location.hostname !== "localhost";

  let cookieString = `token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=None`;

  // Add domain and Secure flag only in production
  if (isProduction) {
    cookieString += `; domain=.theprofit.ai; Secure`;
  }

  document.cookie = cookieString;
};

//Function to refresh access token using GraphQL
export const refreshTokenFn = async () => {
  const refreshToken = localStorage.getItem("refresh");

  if (!refreshToken) {
    console.error("No refresh token found");
    clearSession();
    window.location.reload();
    return null;
  }

  try {
    const { data } = await client.mutate({
      mutation: REFRESH_TOKEN,
      variables: { refreshToken },
    });

    const newToken = data.refreshToken.token.accessToken;
    const newRefreshToken = data.refreshToken.token.refreshToken || null;
    const hasBusinessProfile = data.refreshToken.hasBusinessProfile;
    const profileImage = data.refreshToken.profileImage;

    //Check if there is any business profile
    if (!localStorage.getItem("business_profile")) {
      clearSession();
      window.location.reload();
      return null;
    }

    //Check for access token if it doesn't exist logout user
    if (!newToken) {
      clearSession();
      window.location.reload();
      return null;
    }

    localStorage.setItem("token", newToken);
    //Set cookie as well
    setTokenCookie(newToken);

    //If new refresh token is generated then set it as well
    if (newRefreshToken) {
      localStorage.setItem("refresh", newRefreshToken);
    }

    // Update business profile status if available
    if (hasBusinessProfile !== undefined) {
      localStorage.setItem("hasBusinessProfile", hasBusinessProfile);
    }

    // Update profile image if available
    if (profileImage) {
      localStorage.setItem("profile_image", profileImage);
    }

    localStorage.setItem("expireAt", addDays(new Date(), 1).getTime()); //add one day as expiry time

    return newToken;
  } catch (error) {
    console.error("Token refresh failed:", error);
    clearSession();
    window.location.reload();
    return null;
  }
};

export const checkTokenExists = () => {
  const token = localStorage.getItem("token");
  return token !== null;
};
